import React from "react";
import { chipClass, type ChipTone } from "./index";

/**
 * Collapsible page section + the rail that jumps to it.
 *
 * WHY. /portfolio was ~10,000px of continuous scroll with no tabs, no section
 * navigation and nothing collapsible — the longest un-navigable page in the app, and
 * longest on exactly the days something is happening. There was no entry point, no way
 * to skim, and no way to know when you were done, so it got skipped.
 *
 * THE ONE RULE THAT MAKES COLLAPSING SAFE. Nothing actionable may live only inside a
 * closed drawer. Every drawer carrying a non-zero badge must have a matching row in
 * "Owed action today" above. That is enforced by a test
 * (src/lib/drawerInvariant.test.ts), not by convention, because the whole point of the
 * rework is that this page stopped being trustworthy through exactly this kind of drift.
 *
 * A remembered "closed" NEVER suppresses a badge: the badge renders on the rail and on
 * the drawer header whether it is open or shut. Persistence is a convenience for the
 * viewer, never a filter on what they are shown.
 *
 * Drawer state is remembered per viewer in localStorage, which can throw outright in a
 * private window or with site data blocked — every access is wrapped, and the page
 * renders correctly with nothing stored.
 */

export type DrawerBadge = { text: string; tone: ChipTone };

export type DrawerSpec = {
  id: string;
  label: string;
  /** Non-null only when this drawer has something asking for attention. */
  badge?: DrawerBadge | null;
  defaultOpen?: boolean;
};

const STORE_PREFIX = "helion.portfolio.drawer.";

function readStored(id: string): boolean | null {
  try {
    const v = window.localStorage.getItem(STORE_PREFIX + id);
    return v === "1" ? true : v === "0" ? false : null;
  } catch {
    return null;
  }
}

function writeStored(id: string, open: boolean): void {
  try {
    window.localStorage.setItem(STORE_PREFIX + id, open ? "1" : "0");
  } catch {
    /* private window, blocked site data — the page must still work */
  }
}

/** Open/closed state for a set of drawers, seeded from localStorage and the URL hash. */
export function useDrawerState(specs: DrawerSpec[]) {
  const seed = React.useMemo(() => {
    const hash = typeof window !== "undefined" ? window.location.hash.replace(/^#/, "") : "";
    const out: Record<string, boolean> = {};
    for (const s of specs) {
      const stored = readStored(s.id);
      // A deep link always wins: arriving at #positions must show positions.
      out[s.id] = hash === s.id ? true : stored !== null ? stored : Boolean(s.defaultOpen);
    }
    return out;
    // Seeded once; the spec list is static per render of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [open, setOpen] = React.useState<Record<string, boolean>>(seed);

  const toggle = React.useCallback((id: string) => {
    setOpen((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      writeStored(id, next[id]);
      return next;
    });
  }, []);

  const setAll = React.useCallback(
    (value: boolean) => {
      setOpen(() => {
        const next: Record<string, boolean> = {};
        for (const s of specs) {
          next[s.id] = value;
          writeStored(s.id, value);
        }
        return next;
      });
    },
    [specs]
  );

  const jumpTo = React.useCallback((id: string) => {
    // Opening before scrolling, so a jump can never land on a collapsed section.
    setOpen((prev) => {
      if (prev[id]) return prev;
      writeStored(id, true);
      return { ...prev, [id]: true };
    });
    window.requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  return { open, toggle, setAll, jumpTo };
}

export const SectionRail: React.FC<{
  specs: DrawerSpec[];
  open: Record<string, boolean>;
  onJump: (id: string) => void;
  onSetAll: (value: boolean) => void;
}> = ({ specs, open, onJump, onSetAll }) => {
  const anyClosed = specs.some((s) => !open[s.id]);

  return (
    <div className="sticky top-2 z-20 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)]/90 px-3 py-2 shadow-sm backdrop-blur">
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-1 text-[10px] uppercase tracking-[0.18em] opacity-55">Sections</div>
        {specs.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onJump(s.id)}
            className="flex items-center gap-1.5 rounded-full border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[11px] font-medium opacity-80 transition hover:opacity-100 focus-visible:opacity-100"
          >
            {s.label}
            {/* Badges render whether the drawer is open or shut — a remembered
                "closed" must never hide that something needs attention. */}
            {s.badge ? <span className={chipClass(s.badge.tone)}>{s.badge.text}</span> : null}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onSetAll(anyClosed)}
          className="ml-auto rounded-full border border-[var(--border-color)] px-3 py-1.5 text-[11px] font-medium opacity-70 transition hover:opacity-100"
        >
          {anyClosed ? "Expand all" : "Collapse all"}
        </button>
      </div>
    </div>
  );
};

export const SectionDrawer: React.FC<{
  spec: DrawerSpec;
  open: boolean;
  onToggle: (id: string) => void;
  /** One line saying what is inside, shown whether open or shut. */
  summary?: string;
  children: React.ReactNode;
}> = ({ spec, open, onToggle, summary, children }) => (
  <section id={spec.id} className="scroll-mt-20">
    <button
      type="button"
      onClick={() => onToggle(spec.id)}
      aria-expanded={open}
      className="flex w-full items-center justify-between gap-3 rounded-xl border border-[var(--card-border)] bg-[var(--card-bg)] px-4 py-3 text-left transition hover:opacity-95"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
            {spec.label}
          </span>
          {spec.badge ? <span className={chipClass(spec.badge.tone)}>{spec.badge.text}</span> : null}
        </div>
        {summary ? (
          <div className="mt-0.5 truncate text-xs" style={{ color: "var(--text-muted)" }}>
            {summary}
          </div>
        ) : null}
      </div>
      <span className="shrink-0 text-xs" style={{ color: "var(--text-muted)" }} aria-hidden="true">
        {open ? "Hide −" : "Show +"}
      </span>
    </button>
    {open ? <div className="mt-3 space-y-4">{children}</div> : null}
  </section>
);
